import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";
import { toWireScopeId, toWireScopeType } from "../../pages/setupWizard/utils/wizardDraftScope";

const maxRetries = 2;

const baseQuery = retry(
  createFlatBufferBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? ""),
  { maxRetries },
);

export const wizardDraftsAPI = createApi({
  reducerPath: "wizardDraftsAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagWizardDraft"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getDraft: builder.query({
      query: (body: { orgId: string; step: number; scopeId: string; token: string }) => ({
        url: `/wizard-drafts/${body.orgId}/${body.step}/${toWireScopeId(
          body.orgId,
          body.step,
          body.scopeId,
        )}`,
        headers: { authorization: body.token },
      }),
      providesTags: (_r, _e, arg) => [
        { type: "tagWizardDraft" as const, id: `${arg.step}-${arg.scopeId}` },
      ],
    }),

    upsertDraft: builder.mutation({
      query: (body: {
        orgId: string;
        step: number;
        scopeId: string;
        scopeType: string;
        draftData: any;
        token: string;
      }) => {
        const wireScopeId = toWireScopeId(body.orgId, body.step, body.scopeId);
        return {
          url: `/wizard-drafts/${body.orgId}/${body.step}/${wireScopeId}`,
          method: "PUT",
          body: {
            step_index: body.step,
            scope_type: toWireScopeType(body.scopeType),
            scope_id: wireScopeId,
            org_id: body.orgId,
            draft_data: body.draftData,
          },
          headers: { authorization: body.token },
        };
      },
      invalidatesTags: (_r, _e, arg) => [
        { type: "tagWizardDraft" as const, id: `${arg.step}-${arg.scopeId}` },
      ],
    }),

    deleteDraft: builder.mutation({
      query: (body: { orgId: string; step: number; scopeId: string; token: string }) => ({
        url: `/wizard-drafts/${body.orgId}/${body.step}/${toWireScopeId(
          body.orgId,
          body.step,
          body.scopeId,
        )}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "tagWizardDraft" as const, id: `${arg.step}-${arg.scopeId}` },
      ],
    }),

    deleteByOrg: builder.mutation({
      query: (body: { orgId: string; token: string }) => ({
        url: `/wizard-drafts/${body.orgId}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
    }),
  }),
});

export const {
  useGetDraftQuery,
  useLazyGetDraftQuery,
  useUpsertDraftMutation,
  useDeleteDraftMutation,
  useDeleteByOrgMutation,
} = wizardDraftsAPI;
